import { type NookListItem } from "@nook/api-client"
import { Badge, cn } from "@nook/ui"
import { Loader2 } from "lucide-react"
import { nookBody, nookHost, nookKind, nookTitle, savedLabel, SourceIcon, splitNote } from "@nook/client-ui"

import { NookLink } from "@/components/nook-row.js"
import { isNote, NOTE_BLOCKS, noteHtml } from "@/components/note-editor.js"

/** Every tile is this tall, and so is its loading skeleton. */
export const CARD_HEIGHT = "h-72"

const TILE = cn(
  CARD_HEIGHT,
  "group relative flex flex-col overflow-hidden rounded-xl border transition-[border-color,transform] active:scale-[0.99]",
  "data-[selected=true]:ring-foreground data-[selected=true]:ring-2 data-[selected=true]:ring-offset-2 data-[selected=true]:ring-offset-background",
)

/**
 * One tile in the grid, one height for all, laid out by what the nook is.
 * A picture fills its tile with the title over a scrim; text — a highlight, a
 * note, a page with no image — is the tile. Images are only ever signed once
 * moderation has cleared them (block 06), so a tile shows the picture or the
 * text, never the bytes ahead of the scan.
 */
export function NookCard({ nook, selected }: { nook: NookListItem; selected: boolean }) {
  const media = nook.highlightColor ? null : (nook.imageUrl ?? nook.thumbnailUrl)

  // A nook still being read is one quiet spinner, and not yet something to open.
  if (nook.status === "processing")
    return (
      <div aria-busy className={cn(CARD_HEIGHT, "bg-muted text-muted-foreground flex flex-col items-center justify-center gap-4 rounded-xl border p-4")}>
        <Loader2 aria-label="Processing" className="size-10 animate-spin" strokeWidth={1.5} />
        {nook.sourceUrl ? <span className="w-full truncate text-center text-xs">{nook.sourceUrl}</span> : null}
      </div>
    )

  return (
    <NookLink nook={nook} selected={selected} className={cn(TILE, media ? "bg-muted" : "bg-card hover:border-ring p-4")}>
      {media ? <ImageTile nook={nook} media={media} /> : <TextTile nook={nook} />}
    </NookLink>
  )
}

function ImageTile({ nook, media }: { nook: NookListItem; media: string }) {
  return (
    <>
      <img
        src={media}
        alt=""
        loading="lazy"
        className="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-[1.02] dark:brightness-90"
      />
      {/* A scrim, not a theme colour: the title sits on the picture in both themes. */}
      <span className="absolute inset-x-0 bottom-0 flex flex-col gap-1.5 bg-gradient-to-t from-black/80 via-black/45 to-transparent p-3 pt-12 text-white">
        {/* An uploaded image is its title and picture alone. */}
        {(!nook.imageUrl || nook.sourceUrl) && <Meta nook={nook} className="text-white/80" />}
        <span className="line-clamp-2 text-sm leading-snug font-medium text-pretty">{nookTitle(nook)}</span>
      </span>
      <Failed nook={nook} className="absolute top-3 left-3 bg-black/60 text-white" />
    </>
  )
}

function TextTile({ nook }: { nook: NookListItem }) {
  const body = nookBody(nook)

  return (
    <>
      {nook.highlightColor ? (
        <span
          className="line-clamp-[8] border-l-[3px] pl-3 text-[0.9375rem] leading-relaxed whitespace-pre-line text-pretty"
          style={{ borderColor: `var(--highlight-${nook.highlightColor})` }}
        >
          {body}
        </span>
      ) : (
        <span className="flex min-h-0 flex-col gap-2">
          <span className="line-clamp-3 text-base leading-snug font-medium text-pretty">{nookTitle(nook)}</span>
          {body && isNote(nook) ? (
            // The note's own formatting, as it reads in the editor.
            <span
              className={cn("text-muted-foreground line-clamp-[6] text-[0.8125rem] leading-relaxed text-pretty [&_h2]:text-foreground [&_h2]:font-semibold", NOTE_BLOCKS)}
              dangerouslySetInnerHTML={{ __html: noteHtml(splitNote(nook.userNote).body) }}
            />
          ) : body ? (
            <span className="text-muted-foreground line-clamp-[6] text-[0.8125rem] leading-relaxed text-pretty whitespace-pre-line">{body}</span>
          ) : null}
        </span>
      )}
      <span className="mt-auto flex flex-col gap-2 pt-3">
        <Failed nook={nook} className="self-start" />
        <Meta nook={nook} className="text-muted-foreground" />
      </span>
    </>
  )
}

function Meta({ nook, className }: { nook: NookListItem; className: string }) {
  return (
    <span className={cn("flex items-center gap-2 text-xs", className)}>
      <SourceIcon icon={nook.sourceIcon} />
      <span className="min-w-0 flex-1 truncate">{nookHost(nook) ?? null}</span>
      <span className="shrink-0 tabular-nums">
        {nookHost(nook) ? `${nookKind(nook)} · ` : null}
        {/* A note reads by when it was last touched (block 47). */}
        {savedLabel(isNote(nook) ? nook.editedAt : nook.capturedAt)}
      </span>
    </span>
  )
}

/** Kept, never hidden (PRD §13): the tile says what did not happen. */
function Failed({ nook, className }: { nook: NookListItem; className: string }) {
  return nook.status === "failed" ? (
    <Badge variant="outline" className={cn("text-muted-foreground", className)}>
      Couldn&apos;t extract this page
    </Badge>
  ) : null
}
